<template>
    <h4 class="section-title">{{ translate("federation.details") }}</h4>

    <div class="field" :class="{ 'has-error': validationErrors.federationName }">
        <label class="label">{{ translate("federation.name") }}</label>
        <div class="control">
            <input class="input" v-model="federationName" type="text" maxlength="150" />
        </div>
        <p v-if="validationErrors.federationName" class="help is-danger">
            {{ validationErrors.federationName[0] }}
        </p>
    </div>

    <div class="field" :class="{ 'has-error': validationErrors.federationCountry }">
        <label class="label">{{ translate("manage_academy.country") }}</label>
        <CountrySelect v-model="federationCountry" />
        <p v-if="validationErrors.federationCountry" class="help is-danger">
            {{ validationErrors.federationCountry[0] }}
        </p>
    </div>

    <div class="field">
        <label class="label">{{ translate("federation.logo_image") }}</label>
        <div class="control">
            <figure class="image federation-logo is-inline-block">
                <img v-if="federationLogoImageUrl" :src="federationLogoImageUrl" />
                <img v-else src="https://martialmatch.com/img/no-image.png" />
            </figure>
        </div>
        <div class="buttons are-small">
            <label class="button">
                <span class="icon">
                    <i class="fas fa-image"></i>
                </span>
                <span>{{ translate("federation.change_picture") }}</span>
                <input ref="fileImageInput" type="file" style="display: none" accept="image/*" @change="onFileImageChange" />
            </label>
            <button class="button" @click="removeLogoImage">
                <span class="icon">
                    <i class="fas fa-trash-alt"></i>
                </span>
                <span>{{ translate("common.delete") }}</span>
            </button>

            <p v-if="invalidFileError" class="has-text-danger mb-2">
                {{ translate("federation.invalid_logo") }}
            </p>
        </div>
    </div>

    <hr />

    <div class="buttons">
        <LoadingButton :is-loading="savingInProgress" class="button is-primary" @click="save" :disabled="!canSubmitForm">
            <span class="icon">
                <i class="fas fa-check"></i>
            </span>
            <span>{{ translate("common.save") }}</span>
        </LoadingButton>

        <button class="button" @click="reset">
            <span class="icon">
                <i class="fas fa-times"></i>
            </span>
            <span>{{ translate("common.reset") }}</span>
        </button>
    </div>
</template>

<script setup lang="ts">
    import { useFederationDashboardStore } from "./federationManagementStore";
    import { computed, onMounted, ref } from "vue";
    import federationsApi from "../../Api/federationsApi";
    import { handleError } from "../../Common/errorHandler";
    import { successToast } from "../../Common/toasts";
    import CountrySelect from "../../Common/CountrySelect.vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import debounce from "lodash.debounce";
    import { translate } from "../../Common/translations";
    import { validateImageDimensions, MAX_IMAGE_DIMENSION_PX } from "../../Common/imageValidation";
    import HttpError from "../../Api/Utils/HttpError";
    import { ValidationError, ValidationErrorsMap } from "../../Common/types";

    const store = useFederationDashboardStore();

    //Data to sent
    const federationName = ref("");
    const federationCountry = ref("");
    const federationLogoImageUrl = ref<string | null>(null);

    //Validation errors
    const invalidFileError = ref(false);
    const validationErrors = ref<ValidationErrorsMap>({});

    const canSubmitForm = computed(() => {
        return federationName.value.trim() !== "" && federationCountry.value.trim() !== "" && !invalidFileError.value;
    });

    //Handling image
    function removeLogoImage() {
        invalidFileError.value = false;
        federationLogoImageUrl.value = "";
    }

    function onFileImageChange(event: Event) {
        const fileInput = event.target as HTMLInputElement;
        if (fileInput.files && fileInput.files.length > 0) {
            const file = fileInput.files[0];

            if (file.size > 1024 * 1024) {
                invalidFileError.value = true;
                fileInput.value = "";
                return;
            }
            //Only png/jpg
            if (!["image/png", "image/jpeg"].includes(file.type)) {
                invalidFileError.value = true;
                fileInput.value = "";
                return;
            }

            // Validate image dimensions before upload
            return validateImageDimensions(file, MAX_IMAGE_DIMENSION_PX).then(dimensionValidation => {
                if (!dimensionValidation.valid) {
                    invalidFileError.value = true;
                    fileInput.value = "";
                    return;
                }

                invalidFileError.value = false;
                return federationsApi
                    .uploadFederationLogoImage(store.federationId, file)
                    .then(response => {
                        federationLogoImageUrl.value = response.url;
                        fileInput.value = "";
                    })
                    .catch(handleError);
            });
        }

        return Promise.resolve();
    }

    function assignValuesFromStore() {
        if (store.federationMetadata) {
            federationName.value = store.federationMetadata.federationName;
            federationCountry.value = store.federationMetadata.federationCountry || "";
            federationLogoImageUrl.value = store.federationMetadata.logoUrl;
        }
    }

    function reset() {
        invalidFileError.value = false;
        validationErrors.value = {};

        return store
            .refreshFederationMetadata()
            .then(() => {
                assignValuesFromStore();
            })
            .catch(handleError);
    }

    const savingInProgress = ref(false);

    function save() {
        savingInProgress.value = true;
        validationErrors.value = {};

        debounce(() => {
            return federationsApi
                .updateFederationMetadata(store.federationId, {
                    federationName: federationName.value.trim(),
                    federationCountry: federationCountry.value,
                    logoUrl: federationLogoImageUrl.value,
                })
                .then(() => {
                    successToast(translate("federation.details_updated"));
                    return store.refreshFederationMetadata();
                })
                .catch((err: HttpError) => {
                    if (err.validationErrors) {
                        const errors: ValidationErrorsMap = {};
                        err.validationErrors.forEach((error: ValidationError) => {
                            errors[error.field] = error.message;
                        });
                        validationErrors.value = errors;
                        return;
                    }
                    handleError(err);
                })
                .finally(() => {
                    savingInProgress.value = false;
                });
        }, 200)();
    }

    onMounted(() => {
        invalidFileError.value = false;
        assignValuesFromStore();
    });
</script>

<style scoped></style>
